import React, { useState, useMemo } from 'react'
import {
  Icon,
  Select,
  Modal,
  Button,
  BUTTON_THEME,
  Chip,
  CHIP_TYPES,
} from '@st-operations/uikit/src/v2'
import ReactTooltip from 'react-tooltip'

import { useModal } from '_hooks'

import { useEventFeedsContext } from '../../context/index.jsx'
import Collapsible from '../collapsible/index.jsx'
import { doesAttributeSupportAggregation, AGGREGATION_OPTIONS } from '../utils.js'
import ManageComponent, { useIsSnapshot } from '../../manage-component/index.jsx'
import CustomCheckbox from '_components/custom-checkbox/index.jsx'

import './styles.scss'

const AggregationConfiguration = ({}, ref) => {
  const {
    dispatch,
    ACTIONS,
    data,
    querySectionsCollapsed: { aggregation: aggregationCollapsed },
  } = useEventFeedsContext()
  const [aggregationsWaitingForConfirmation, setAggregationsWaitingForConfirmation] = useState(null)
  const confirmationModal = useModal(false)
  const isSnapshot = useIsSnapshot()

  // PREVIOUS LOGIC
  // const normalizedAggregationOptions = useMemo(
  //   () =>
  //     Object.values(schema?.attributes)
  //       .filter((val) => typeof val !== 'string' && !Array.isArray(val))
  //       .map((val) =>
  //         Object.keys(val).map((valKey, index) => ({
  //           label: Object.values(val)?.[index]?.label || valKey,
  //           value: valKey,
  //         }))
  //       )
  //       .flat()
  //       .filter((val) => AGGREGATION_OPTIONS.includes(val.value)),
  //   [schema, data]
  // )
  const normalizedAggregationOptions = useMemo(
    () => AGGREGATION_OPTIONS.map((val) => ({ label: val, value: val })),
    []
  )

  const handleSelectChange = (_, name, value) => {
    if (data.attributes.some((attr) => !doesAttributeSupportAggregation(attr))) {
      setAggregationsWaitingForConfirmation(value)
      confirmationModal.handleOpenModal()
      return
    }

    dispatch({
      type: ACTIONS.UPDATE_AGGREGATIONS,
      payload: {
        aggregations: Array.from(new Set([...(data?.aggregations || []), value])),
        attributes: Array.from(new Set([...data.attributes, value])),
      },
    })
  }

  const handleConfirmUpdate = () => {
    dispatch({
      type: ACTIONS.UPDATE_AGGREGATIONS,
      payload: {
        attributes: Array.from(
          new Set([
            ...data.attributes.filter(doesAttributeSupportAggregation),
            aggregationsWaitingForConfirmation,
          ])
        ),
        aggregations: [aggregationsWaitingForConfirmation],
      },
    })
    setAggregationsWaitingForConfirmation(null)
    confirmationModal.handleCloseModal()
  }

  const handleCancelUpdate = () => {
    setAggregationsWaitingForConfirmation(null)
    confirmationModal.handleCloseModal()
  }

  const setAggregationCollapsed = (value) => {
    dispatch({ type: ACTIONS.UPDATE_QUERY_SECTION_COLLAPSED, payload: { aggregation: !value } })
  }

  const handleRemoveAll = () => {
    dispatch({
      type: ACTIONS.UPDATE_AGGREGATIONS,
      payload: {
        attributes: data.attributes.filter((a) => !a.includes(data.aggregations)),
        aggregations: [],
      },
    })
  }

  const handleRemoveAttribute = (index) => () => {
    dispatch({
      type: ACTIONS.REMOVE_ATTRIBUTE,
      payload: {
        index,
      },
    })
  }

  const handleProjectOutputByFieldsChange = (checked) => {
    dispatch({
      type: ACTIONS.SET_DATA,
      payload: {
        ...data,
        projectOutputByFields: checked,
      },
    })
  }

  return (
    <Collapsible
      ref={ref}
      title="Aggregate By"
      showOpenButton={false}
      onDeleteAllClick={handleRemoveAll}
      showDeleteAllButton={!isSnapshot && data.aggregations?.length > 0}
      managedState={[!aggregationCollapsed, setAggregationCollapsed]}
    >
      <ManageComponent>
        <If condition={data.aggregations?.length > 0}>
          <div className="flex flex-row gap-1 items-center">
            <CustomCheckbox
              label="Output only selected fields"
              checked={data.projectOutputByFields === true}
              onChange={(e) => handleProjectOutputByFieldsChange(e.target.checked)}
            />
            <div
              className="cursor-pointer w-[24px] h-[24px]"
              data-tip
              data-for="project_output_by_fields"
            >
              <Icon type="outline" icon="info" color="gray" />
            </div>
          </div>
          <ReactTooltip id="project_output_by_fields" effect="solid" place="right">
            <div>{`Toggles 'project_output_by_fields'`}</div>
            <div>{`When toggled on, field's user count and event count`}</div>
            <div>{`won't be included in the feed`}</div>
          </ReactTooltip>
        </If>
        <p className="aggregate-configuration__description">Works for specific attributes</p>
        <Select
          placeholder={
            <div className="attribute-configuration__placeholder-search">
              <Icon icon="search" type="outline" color="#3D3D3D" /> Quick search{' '}
            </div>
          }
          options={normalizedAggregationOptions}
          isSearchable
          onChange={handleSelectChange}
          useReactSelect
        />
      </ManageComponent>
      <Choose>
        <When condition={data?.aggregations?.length > 0}>
          <ManageComponent>
            <div className="aggregate-configuration__selections-header">
              <h2 className="aggregate-configuration__selections-title">
                Selected aggregations fields
              </h2>
            </div>
          </ManageComponent>
          <div className="aggregate-configuration__chips">
            <For each="aggregation" index="index" of={data.aggregations || []}>
              <Chip
                small
                type={CHIP_TYPES.OUTLINED}
                value={aggregation}
                style={{ display: isSnapshot ? 'none' : undefined }}
                onClick={handleRemoveAttribute(index)}
              />
            </For>
          </div>
        </When>
        <Otherwise>
          <p className="aggregate-configuration__description">
            {`You haven't added any aggregations yet.`}
          </p>
        </Otherwise>
      </Choose>

      <Modal
        isOpen={confirmationModal.isOpen}
        onClose={handleCancelUpdate}
        classes={{ childWrapper: 'aggregate-configuration__confirmation-modal-child ' }}
      >
        <div title="Confirm aggregations">
          <p className="aggregate-configuration__confirmation">
            Only CPG and B2B attributes are supported, if you choose to continue other attributes
            will be removed from the feed.
          </p>
          <div>
            <Button theme={BUTTON_THEME.GHOST} onClick={handleCancelUpdate}>
              Cancel
            </Button>
            <Button onClick={handleConfirmUpdate}>Confirm</Button>
          </div>
        </div>
      </Modal>
    </Collapsible>
  )
}

export default React.forwardRef(AggregationConfiguration)
